
import { TEXTURE_ART, elem, styleInstaller } from "./chrome";

export const GAMEBUTTON_SIZE = 32;
/** `gamewindow.py` StatusPlusButton x. */
export const GAMEBUTTON_STATUS_X = 68;
/** SkillPlusButton and BuildGuildBuilding sit with their left edge at
 *  x = SCREEN_WIDTH - 50 - 32 (`gamewindow.py:84,128`). */
export const GAMEBUTTON_SKILL_RIGHT = 50 + 32; // gamewindow.py:84
/** Top of each button is SCREEN_HEIGHT - 100 (`gamewindow.py`). */
export const GAMEBUTTON_BOTTOM_TOP = 100;

/** `gamewindow.py` BuildGuildBuilding y = SCREEN_HEIGHT - 170. */
export const GAMEBUTTON_BUILD_TOP = 170;

/** English `locale_interface.txt` wording the original paints under the icons. */
export const GAMEBUTTON_STATUS_LABEL = "Raise State";
export const GAMEBUTTON_SKILL_LABEL = "Raise Skills";
/** GUILD_BUILDING_TITLE (`locale_interface.txt:85`). */
export const GAMEBUTTON_BUILD_LABEL = "Guild House Building";

const ART = {
  up: `${TEXTURE_ART}/game/windows/btn_bigplus_up.png`,
  over: `${TEXTURE_ART}/game/windows/btn_bigplus_over.png`,
  down: `${TEXTURE_ART}/game/windows/btn_bigplus_down.png`,
} as const;

const STYLE_ID = "gamebutton-style";

const STYLE = `
/* Under the inventory: ToggleInventoryWindow SetTops it on open
   (interfacemodule.py:958-959), so its Yang line covers the label. */
.gamebutton-window {
  position: fixed;
  inset: 0;
  z-index: 39;
  pointer-events: none;
}
.gamebutton-window[hidden] { display: none; }
.gamebutton {
  position: absolute;
  width: ${GAMEBUTTON_SIZE}px;
  height: ${GAMEBUTTON_SIZE}px;
  padding: 0;
  border: 0;
  background: transparent url("${ART.up}") center / ${GAMEBUTTON_SIZE}px ${GAMEBUTTON_SIZE}px no-repeat;
  image-rendering: pixelated;
  cursor: pointer;
  pointer-events: auto;
  top: calc(100% - ${GAMEBUTTON_BOTTOM_TOP}px);
}
.gamebutton:hover { background-image: url("${ART.over}"); }
.gamebutton:active { background-image: url("${ART.down}"); }
.gamebutton[hidden] { display: none; }
.gamebutton-status { left: ${GAMEBUTTON_STATUS_X}px; }
.gamebutton-skill { left: calc(100% - ${GAMEBUTTON_SKILL_RIGHT}px); }
.gamebutton-build { left: calc(100% - ${GAMEBUTTON_SKILL_RIGHT}px); top: calc(100% - ${GAMEBUTTON_BUILD_TOP}px); }
/* SkillPlusLabel etc. sit at child y:40 (gamewindow.py:84-101), below the
   32px button image CButton::SetUpVisual sizes the button to
   (PythonWindow.cpp:1611-1636). LoadElementText never sizes a TextLine
   (ui.py:3267-3313), so 2014's own pick rect for the caption never grows
   past zero (PythonWindow.cpp:12-33) and CWindow::PickWindow -- which only
   recurses into a child whose own rect the point already falls inside
   (PythonWindow.cpp:781-807) -- finds neither the label nor the button
   there and lets the click through to the world. pointer-events: none
   keeps a real click doing the same: without it, a click on the visible
   text would target this span and bubble to the button, opening the window
   for a pixel 2014 never routes there (gamebutton.test.ts). */
.gamebutton-label {
  position: absolute;
  left: 50%;
  top: 40px;
  transform: translateX(-50%);
  white-space: nowrap;
  color: #fff;
  font: 11px/1 Tahoma, "Segoe UI", sans-serif;
  text-shadow: 1px 1px 1px #000;
  pointer-events: none;
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export interface GameButtonActions {
  /** `ToggleCharacterWindow("STATUS")` (`interfacemodule.py:1065-1066`). */
  onStatus?: () => void;
  /** `ToggleCharacterWindow("SKILL")` (`interfacemodule.py:1068-1069`). */
  onSkill?: () => void;
  /** `BUILD_OpenWindow` (`interfacemodule.py:1079-1080`). */
  onBuild?: () => void;
}

/**
 * Whether `CheckGameButton` would Show the STATUS / SKILL buttons.
 * Exported so tests can pin the original `> 0` rule without mounting DOM.
 */
export function gameButtonVisible(points: number): boolean {
  return points > 0;
}

export class GameButtonWindow {
  private readonly root: HTMLElement;
  private readonly statusBtn: HTMLButtonElement;
  private readonly skillBtn: HTMLButtonElement;
  private readonly buildBtn: HTMLButtonElement;
  private statPoints = 0;
  private skillPoints = 0;

  constructor(mount: HTMLElement, actions: GameButtonActions = {}) {
    const doc = mount.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "gamebutton-window");
    this.root.hidden = true;
    this.root.setAttribute("role", "group");
    this.root.setAttribute("aria-label", "Unspent points");

    this.statusBtn = makeButton(doc, "gamebutton-status", GAMEBUTTON_STATUS_LABEL, () => {
      actions.onStatus?.();
    });
    this.skillBtn = makeButton(doc, "gamebutton-skill", GAMEBUTTON_SKILL_LABEL, () => {
      actions.onSkill?.();
    });

    this.buildBtn = makeButton(doc, "gamebutton-build", GAMEBUTTON_BUILD_LABEL, () => {
      actions.onBuild?.();
    });

    this.root.append(this.statusBtn, this.skillBtn, this.buildBtn);
    mount.append(this.root);
  }

  get element(): HTMLElement {
    return this.root;
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get statusShowing(): boolean {
    return !this.statusBtn.hidden;
  }

  get skillShowing(): boolean {
    return !this.skillBtn.hidden;
  }

  get buildShowing(): boolean {
    return !this.buildBtn.hidden;
  }

  /** `ShowBuildButton` / `HideBuildButton` (`uigamebutton.py:60-64`). */
  setBuild(visible: boolean): void {
    if (this.buildBtn.hidden !== visible) return;
    this.buildBtn.hidden = !visible;
    this.syncRoot();
  }

  private syncRoot(): void {
    this.root.hidden = this.statusBtn.hidden && this.skillBtn.hidden && this.buildBtn.hidden;
  }

  check(statPoints: number, skillPoints: number): void {
    this.statPoints = statPoints;
    this.skillPoints = skillPoints;
    this.statusBtn.hidden = !gameButtonVisible(statPoints);
    this.skillBtn.hidden = !gameButtonVisible(skillPoints);
    this.syncRoot();
  }

  /** Current pools last applied by `check`. */
  get pools(): { readonly statPoints: number; readonly skillPoints: number } {
    return { statPoints: this.statPoints, skillPoints: this.skillPoints };
  }
}

function makeButton(
  doc: Document,
  className: string,
  label: string,
  onClick: () => void,
): HTMLButtonElement {
  const button = doc.createElement("button");
  button.type = "button";
  button.className = `gamebutton ${className}`;
  button.hidden = true;
  button.setAttribute("aria-label", label);
  const text = elem(doc, "span", "gamebutton-label", label);
  button.append(text);
  button.addEventListener("click", onClick);
  return button;
}
